import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { SKY_FIRE_FLOW } from '../../../packages/domain/src/110-sky-fire-flow.mjs';

/** Bounded instanced sheets consume exact domain material segments. No sheet
 * attaches old gas to the current mouth. Reduced motion keeps the same visible
 * hazard envelope; only decorative turbulence, cards and smoke are suppressed. */
export function createSkyBreathFlow(T, scene, own, time, moving) {
  const noise = `
    float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
    float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);
      return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y);}
    float gas(vec2 p){return noise(p)*.57+noise(p*2.03)*.28+noise(p*4.17)*.15;}
  `;
  const sheets = [];
  for (let index = 0; index < 4; index++) {
    const sheet = new T.PlaneGeometry(2, 1, 1, 2);
    sheet
      .rotateX(Math.PI / 2)
      .translate(0, 0, 0.5)
      .rotateZ((index * Math.PI) / 4);
    sheets.push(sheet);
  }
  const geometry = own(mergeGeometries(sheets));
  for (const sheet of sheets) sheet.dispose();
  const radii = new T.InstancedBufferAttribute(new Float32Array(SKY_FIRE_FLOW.maxParcels * 2), 2);
  geometry.setAttribute('flowRadii', radii);
  const material = own(
    new T.ShaderMaterial({
      uniforms: { time, moving },
      transparent: true,
      depthWrite: false,
      side: T.DoubleSide,
      blending: T.AdditiveBlending,
      toneMapped: false,
      vertexShader: `attribute vec2 flowRadii;uniform float time,moving;varying vec2 gasUv;
      void main(){vec3 p=position;gasUv=vec2(uv.x,p.z);
        p.xy*=mix(flowRadii.x,flowRadii.y,p.z)*(.92+.08*sin(p.z*30.0-time*18.0*moving));
        gl_Position=projectionMatrix*modelViewMatrix*instanceMatrix*vec4(p,1.0);}`,
      fragmentShader: `${noise}uniform float time,moving;varying vec2 gasUv;
      void main(){float lateral=abs(gasUv.x*2.0-1.0);
        float n=gas(vec2(gasUv.x*5.0,gasUv.y*4.0-time*10.0*moving));
        float edge=1.0-smoothstep(.35,.99,lateral+n*.22);
        float heat=clamp((1.0-lateral)*.7+n*.3,0.0,1.0);
        vec3 color=mix(vec3(.65,.035,.003),vec3(1.25,.48,.035),heat);
        color=mix(color,vec3(1.5,.95,.32),pow(heat,5.0));
        gl_FragColor=vec4(color,edge*(.14+smoothstep(.18,.8,n)*.22));}`,
    }),
  );
  const column = new T.InstancedMesh(geometry, material, SKY_FIRE_FLOW.maxParcels);
  column.name = 'Inertial mouth material segments';
  column.visible = false;
  column.count = 0;
  column.frustumCulled = false;
  scene.add(column);
  const cardGeometry = own(new T.PlaneGeometry(2, 2));
  function cardMaterial(smoke) {
    return own(
      new T.ShaderMaterial({
        uniforms: { time, moving },
        transparent: true,
        depthWrite: false,
        side: T.DoubleSide,
        blending: smoke ? T.NormalBlending : T.AdditiveBlending,
        toneMapped: false,
        vertexShader: `varying vec2 parcelUv;varying float seed;
        void main(){parcelUv=uv;seed=instanceMatrix[3].x+instanceMatrix[3].z;
        gl_Position=projectionMatrix*modelViewMatrix*instanceMatrix*vec4(position,1.0);}`,
        fragmentShader: `${noise}uniform float time,moving;varying vec2 parcelUv;varying float seed;
        void main(){vec2 p=parcelUv*2.0-1.0;
          float n=gas(p*3.0+vec2(seed,time*${smoke ? '1.2' : '5.0'}*moving));
          float edge=1.0-smoothstep(.22,1.0,length(p)+n*.3);
          float tongues=smoothstep(.25,.76,n+p.y*.16);
          vec3 color=${smoke ? 'mix(vec3(.10,.085,.075),vec3(.25,.19,.14),n)' : 'mix(vec3(.8,.06,.003),vec3(1.3,.55,.05),tongues)'};
          gl_FragColor=vec4(color,edge*${smoke ? '.13' : '(.15+tongues*.23)'});}`,
      }),
    );
  }
  const flame = new T.InstancedMesh(cardGeometry, cardMaterial(false), 48);
  const smoke = new T.InstancedMesh(cardGeometry, cardMaterial(true), 12);
  flame.name = 'Bounded inertial flame parcels';
  smoke.name = 'Cooling inertial smoke';
  for (const mesh of [flame, smoke]) {
    mesh.count = 0;
    mesh.frustumCulled = false;
    scene.add(mesh);
  }
  const pose = new T.Object3D(),
    axis = new T.Vector3(0, 0, 1),
    direction = new T.Vector3(),
    across = new T.Vector3(),
    up = new T.Vector3(),
    normal = new T.Vector3(),
    matrix = new T.Matrix4();
  const quaternion = new T.Quaternion();
  let last = [],
    lastAt = -10;
  function draw(state, camera, quiet) {
    if (state.time < lastAt || state.traversal || state.boss?.defeated) last = [];
    const current = state.boss?.breath;
    const active = current?.flowActive && !state.traversal && !state.boss.defeated;
    const segments = active ? (current.segments ?? []).slice(0, SKY_FIRE_FLOW.maxParcels) : [];
    column.count = segments.length;
    column.visible = segments.length > 0;
    for (const [index, segment] of segments.entries()) {
      pose.position.set(segment.from.x, segment.from.y, segment.from.z);
      direction.set(
        segment.to.x - segment.from.x,
        segment.to.y - segment.from.y,
        segment.to.z - segment.from.z,
      );
      // Skewed cross-sections share the locked telegraph plane with collision.
      // A bent streamline never tilts its hot front ahead of that plane.
      normal.set(segment.direction.x, segment.direction.y, segment.direction.z);
      across
        .crossVectors(normal, Math.abs(normal.y) > 0.95 ? axis : new T.Vector3(0, 1, 0))
        .normalize();
      up.crossVectors(normal, across).normalize();
      matrix.makeBasis(across, up, direction).setPosition(pose.position);
      column.setMatrixAt(index, matrix);
      radii.setXY(index, segment.fromRadius, segment.toRadius);
    }
    radii.needsUpdate = true;
    column.instanceMatrix.needsUpdate = true;
    if (segments.length) {
      last = segments;
      lastAt = state.time;
    }
    const residue = Math.max(0, state.time - lastAt);
    flame.count = !quiet && segments.length ? Math.min(48, segments.length * 2) : 0;
    smoke.count = !quiet && last.length && !state.traversal && residue < 0.45 ? 12 : 0;
    if (camera) camera.getWorldQuaternion(quaternion);
    else quaternion.identity();
    for (const [mesh, cooling] of [
      [flame, false],
      [smoke, true],
    ]) {
      const source = cooling ? last : segments;
      for (let index = 0; index < mesh.count; index++) {
        const segment = source[Math.floor((index * source.length) / mesh.count)],
          phase = (index * 0.618034) % 1;
        const radius = segment.fromRadius + (segment.toRadius - segment.fromRadius) * phase;
        pose.position.set(
          ...['x', 'y', 'z'].map(
            (key) =>
              segment.from[key] +
              (segment.to[key] - segment.from[key]) * phase +
              (cooling ? (segment.velocity?.[key] ?? 0) * residue : 0),
          ),
        );
        const size = radius * (cooling ? 0.6 + residue : 0.5);
        pose.scale.set(size, size * (cooling ? 0.65 : 1), 1);
        pose.quaternion.copy(quaternion);
        pose.rotateZ(index * 2.39996);
        pose.updateMatrix();
        mesh.setMatrixAt(index, pose.matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
    }
  }
  return {
    draw,
    column,
    flame,
    smoke,
    warmup: () => [
      new T.InstancedMesh(geometry, material, 1),
      new T.InstancedMesh(cardGeometry, flame.material, 1),
      new T.InstancedMesh(cardGeometry, smoke.material, 1),
    ],
    dispose() {
      for (const mesh of [column, flame, smoke]) {
        mesh.removeFromParent();
        mesh.dispose();
      }
      last = [];
    },
  };
}
